/**
 * Admin Users Management
 * 
 * List, search, and manage platform users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { getAuthToken } from '../../utils/auth';

const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';

const fetchUsers = async (params) => {
    const token = getAuthToken();
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/users?${queryString}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
};

const updateUser = async (userId, data) => {
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update user');
    return response.json();
};

export function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [tierFilter, setTierFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const loadUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = { page, page_size: 20 };
            if (search) params.search = search;
            if (tierFilter) params.tier = tierFilter;
            if (roleFilter) params.role = roleFilter;

            const data = await fetchUsers(params);
            setUsers(data.users || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to load users:', err);
        } finally {
            setIsLoading(false);
        }
    }, [page, search, tierFilter, roleFilter]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadUsers();
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setShowModal(true);
    };

    const handleSaveUser = async (updates) => {
        try {
            await updateUser(selectedUser.id, updates);
            setShowModal(false);
            loadUsers();
        } catch (err) {
            console.warn('Failed to update user:', err.message);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatSessionStatus = (user) => {
        if (!user.session_status) return { label: 'Offline', className: 'offline' };
        if (user.session_status === 'RUNNING') {
            const ago = user.session_last_heartbeat
                ? timeAgo(new Date(user.session_last_heartbeat))
                : '';
            return { label: `Healthy${ago ? ` (${ago})` : ''}`, className: 'active' };
        }
        if (user.session_status === 'STARTING') {
            return { label: 'Booting (not healthy)', className: 'provisioning' };
        }
        if (user.session_status === 'PROVISIONING') {
            return { label: 'Provisioning...', className: 'provisioning' };
        }
        return { label: user.session_status, className: 'offline' };
    };

    const timeAgo = (date) => {
        const seconds = Math.floor((new Date() - date) / 1000);
        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    return (
        <div className="admin-users">
            <div className="admin-header">
                <h1>User Management</h1>
                <p>Manage platform users, roles, and tiers</p>
            </div>

            {/* Filters */}
            <div className="admin-filters">
                <form onSubmit={handleSearch} className="search-form">
                    <input
                        type="text"
                        placeholder="Search by email or name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <button type="submit">Search</button>
                </form>

                <select
                    value={tierFilter}
                    onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Tiers</option>
                    <option value="FREE">Free</option>
                    <option value="STARTER">Starter</option>
                    <option value="PRO">Pro</option>
                    <option value="BUSINESS">Business</option>
                    <option value="ENTERPRISE">Enterprise</option>
                </select>

                <select
                    value={roleFilter}
                    onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                </select>
            </div>

            {/* Users Table */}
            <div className="admin-table-container">
                {isLoading ? (
                    <div className="table-loading">
                        <div className="spinner"></div>
                    </div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Tier</th>
                                <th>Role</th>
                                <th>Projects</th>
                                <th>Requests (Month)</th>
                                <th>Session</th>
                                <th>Last Login</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">
                                                {user.avatar_url ? (
                                                    <img src={user.avatar_url} alt="" />
                                                ) : (
                                                    <span>{(user.display_name || user.email)[0].toUpperCase()}</span>
                                                )}
                                            </div>
                                            <div className="user-info">
                                                <span className="user-name">{user.display_name || 'No name'}</span>
                                                <span className="user-email">{user.email}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`tier-badge ${user.tier}`}>
                                            {user.tier}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`role-badge ${user.role}`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td>{user.project_count || 0}</td>
                                    <td>{user.current_month_requests || 0}</td>
                                    <td>
                                        {(() => {
                                            const s = formatSessionStatus(user);
                                            return (
                                                <span className={`session-badge ${s.className}`}>
                                                    {s.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td>{formatDate(user.last_login_at)}</td>
                                    <td>
                                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                            {user.is_active ? 'Active' : 'Disabled'}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleEditUser(user)}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
                <span>Showing {users.length} of {total} users</span>
                <div className="pagination-controls">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </button>
                    <span>Page {page}</span>
                    <button
                        disabled={users.length < 20}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* Edit Modal */}
            {showModal && selectedUser && (
                <UserEditModal
                    user={selectedUser}
                    onSave={handleSaveUser}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
}

function UserEditModal({ user, onSave, onClose }) {
    const [tier, setTier] = useState(user.tier);
    const [role, setRole] = useState(user.role);
    const [isActive, setIsActive] = useState(user.is_active);
    const [isSaving, setIsSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        await onSave({ tier, role, is_active: isActive });
        setIsSaving(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <h2>Edit User</h2>
                <p className="modal-subtitle">{user.email}</p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Tier</label>
                        <select value={tier} onChange={e => setTier(e.target.value)}>
                            <option value="FREE">Free</option>
                            <option value="STARTER">Starter</option>
                            <option value="PRO">Pro</option>
                            <option value="BUSINESS">Business</option>
                            <option value="ENTERPRISE">Enterprise</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Role</label>
                        <select value={role} onChange={e => setRole(e.target.value)}>
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            <option value="superadmin">Super Admin</option>
                        </select>
                    </div>

                    <div className="form-group checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={e => setIsActive(e.target.checked)}
                            />
                            Account Active
                        </label>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AdminUsers;

